import { useLayoutEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams, useLocation } from 'react-router-dom';

import useBackButtonExitHandler from './hooks/useBackButtonExitHandler';
import useBackButtonHandler from './hooks/useBackButtonHandler';

import { APP_BASE_PATH, OLD_APP_BASE_PATH } from './utilities/appBasePath';
import { rewriteUrl } from './utilities/routes';
import { shouldTransposeChrome } from './utilities/transposedRoutes';
import { isNoIndexed } from './utilities/robotsMeta';
import { useRobotsMeta } from './hooks/useRobotsMeta';
import HomeRedirect from './components/HomeRedirect';
import mfeConfig from './mfeConfig';
import AppraisalModule from './modules/Appraisal';
import HubModule from './modules/Hub';
import BasePage from './components/_shared/BasePage';
import Unauthorized from './components/Auth/Unauthorized';
import { AuthGuard } from './context/AuthProvider';

const IdRedirect = () => {
  const { id } = useParams();
  if (!id || !id.match(/\d+/)) {
    return <Navigate to={APP_BASE_PATH} />;
  }
  return <Navigate to={`${APP_BASE_PATH}/hub/${id}`} />;
};

const IdRedirectWithPage = () => {
  const { id, page } = useParams();
  if (!id || !id.match(/\d+/)) {
    return <Navigate to={APP_BASE_PATH} />;
  }
  return <Navigate to={`${APP_BASE_PATH}/hub/${id}?force=${page}`} />;
};

const BasePageController = ({ children }: { children: React.ReactElement }) => {
  const { pathname } = useLocation();
  useRobotsMeta(isNoIndexed(pathname));
  return <BasePage transpose={shouldTransposeChrome(pathname)}>{children}</BasePage>;
};

const NavigateToNewBasePath = () => {
  const location = useLocation();

  return (
    <Navigate
      to={rewriteUrl({
        from: OLD_APP_BASE_PATH,
        to: APP_BASE_PATH,
        location
      })}
    />
  );
};

export const RouterContent = () => {
  // Track back button navigation events with analytics
  useBackButtonHandler({ component: 'Shell' });

  // Track back-button navigation that leaves the Sell-to-Carvana app entirely
  useBackButtonExitHandler();

  return (
    <BasePageController>
      <AuthGuard>
        <Routes>
          <Route path={APP_BASE_PATH}>
            <Route path="getoffer/*" element={<AppraisalModule />} />
            <Route path="offer/*" element={<AppraisalModule />} />
            <Route path="refresh/*" element={<AppraisalModule />} />
            <Route path="unauthorized" element={<Unauthorized />} />
            <Route path=":id" element={<IdRedirect />} />
            <Route path=":id/:page" element={<IdRedirectWithPage />} />
            <Route path="confirmation/*" element={<HubModule />} />
            <Route path="hub/*" element={<HubModule />} />
            <Route path="plaid-redirect" element={<HubModule />} />
            <Route index element={<HomeRedirect />} />
          </Route>
          <Route path={OLD_APP_BASE_PATH} element={<NavigateToNewBasePath />} />
          <Route index element={<Navigate to={APP_BASE_PATH} />} />
        </Routes>
      </AuthGuard>
    </BasePageController>
  );
};

const ShellRoutes = () => {
  useLayoutEffect(() => {
    mfeConfig();
  }, []);

  return (
    <BrowserRouter>
      <RouterContent />
    </BrowserRouter>
  );
};

export default ShellRoutes;
